// frontend/src/services/supplierResourceService.ts
// Fetch / batch-import supplier-resource catalog links.
//
// Mirrors the pattern in RecipeImportExport.handleImport: batch the items in
// groups of 10, surface progress via onProgress callback, aggregate the lists
// of auto-created suppliers/resources across batches.

import api from './api';
import type { ImportResult } from '../components/import-export/ImportExportDialog';

export interface SupplierResourceLink {
  /** ISO date string — set on items returned by the export endpoint so the
   *  dialog can show "Créée le …" tags. */
  _createdAt?: string;
  supplier: {
    name: string;
    code: string | null;
  };
  resource: {
    name: string;
    veloceResourceId: string | null;
  };
  link: {
    supplierSku: string | null;
    unitCost: number;
    minOrderQty: number | null;
    leadTimeDays: number | null;
    isPreferred: boolean;
  };
}

/** Fetch every supplier-resource link for the active location. */
export async function fetchSupplierResourceLinks(): Promise<SupplierResourceLink[]> {
  const res = await api.get('/data/export/supplier-resources');
  return res.data?.data ?? [];
}

/** Batched import — same UX as the recipe import. */
export async function importSupplierResourceLinks(
  items: { data: SupplierResourceLink; resolution?: 'incoming' | 'existing' }[],
  options: { autoCreateSuppliers?: boolean; onProgress?: (current: number, total: number) => void } = {},
): Promise<ImportResult> {
  // Drop links the user explicitly asked to keep as-is.
  const toImport = items
    .filter(it => it.resolution !== 'existing')
    .map(it => it.data);

  if (toImport.length === 0) {
    return { succeeded: 0, failed: [], autoCreatedResources: [], autoCreatedSuppliers: [] };
  }

  const result: ImportResult = {
    succeeded: 0,
    failed: [],
    autoCreatedResources: [],
    autoCreatedSuppliers: [],
  };
  const createdRes = new Set<string>();
  const createdSup = new Set<string>();
  const batchSize = 10;
  const total = toImport.length;
  const autoCreateSuppliers = options.autoCreateSuppliers ?? true;

  for (let i = 0; i < total; i += batchSize) {
    const batch = toImport.slice(i, i + batchSize);
    try {
      const res = await api.post('/data/import/supplier-resources', {
        data: batch,
        autoCreateSuppliers,
      });
      result.succeeded += batch.length;
      const details = res.data?.data?.details ?? {};
      for (const n of details.autoCreatedResources ?? []) createdRes.add(n);
      for (const n of details.autoCreatedSuppliers ?? []) createdSup.add(n);
      // Backend reports per-line errors that didn't cause the batch to fail
      // (e.g. supplier missing when autoCreateSuppliers is false).
      for (const err of details.errors ?? []) {
        result.failed.push({ name: err.name, error: err.error });
        result.succeeded--;
      }
    } catch (err: any) {
      batch.forEach(item => {
        result.failed.push({
          name: `${item.supplier.name} → ${item.resource.name}`,
          error: err?.response?.data?.message || err?.message || 'Erreur inconnue',
        });
      });
    }
    options.onProgress?.(Math.min(i + batchSize, total), total);
  }

  result.autoCreatedResources = [...createdRes].sort();
  result.autoCreatedSuppliers = [...createdSup].sort();
  return result;
}
